import { ReactNode, useCallback, useMemo } from "react";
import {
  Select as ArkSelect,
  useSelectContext,
  SelectRootProps,
  CollectionItem,
  Portal,
} from "@ark-ui/react";
import { ChevronDown } from "lucide-react";
import classnames from "classnames";

import styles from "./select.module.css";

export interface SelectOption {
  label: string;
  value: string;
}

type SelectProps<T = CollectionItem> = Pick<
  SelectRootProps<T>,
  "collection" | "name" | "disabled" | "positioning"
> & {
  label?: string;
  placeholder?: string;
  trigger?: ReactNode;
  content?: ReactNode;
  error?: string;
  variant?: "ghost";
  onlyLightTheme?: boolean;
  /** Applied to the root wrapper so the component can be `styled()`. */
  className?: string;
} & (
    | {
        multiple: true;
        value?: string[];
        onValueChange?: (value: string[]) => void;
      }
    | {
        multiple?: false;
        value?: string;
        onValueChange?: (value: string) => void;
      }
  );

export default function Select({
  collection,
  value,
  onValueChange,
  placeholder,
  label,
  disabled = false,
  trigger,
  content,
  name,
  error,
  multiple,
  positioning,
  variant,
  onlyLightTheme,
  className,
}: SelectProps) {
  const onChange = useCallback(
    (details: ArkSelect.ValueChangeDetails) => {
      if (!onValueChange) return;

      if (multiple) onValueChange(details.value);

      if (details.value.length > 0 && !multiple) {
        onValueChange(details.value[0]);
      }
    },
    [onValueChange],
  );

  // Because the root always expects an array for value
  const usedValue = useMemo(() => {
    if (multiple) return value ?? [];
    return value ? [value] : undefined;
  }, [value, multiple]);

  const rootPositioning = useMemo(
    () => ({
      sameWidth: true,
      ...positioning,
    }),
    [positioning],
  );

  return (
    <div
      className={classnames(
        styles.selectWrapper,
        onlyLightTheme && styles.onlyLightTheme,
        className,
      )}
    >
      <ArkSelect.Root
        collection={collection}
        name={name}
        value={usedValue}
        onValueChange={onChange}
        disabled={disabled}
        multiple={multiple}
        positioning={rootPositioning}
        className={classnames({
          [styles[`${variant}Variant`]]: variant,
          [styles.errorVariant]: !!error,
        })}
      >
        {label && <ArkSelect.Label>{label}</ArkSelect.Label>}
        <ArkSelect.Control className={styles.control}>
          {/* Escape hatch: when a caller supplies its own `trigger` (rendered via
              asChild), that element owns its styling. Tag it with `.customTrigger`
              so the default combobox chrome in select.module.css opts out instead
              of layering a border/radius/padding on top of the caller's element. */}
          <ArkSelect.Trigger
            asChild={!!trigger}
            className={trigger ? styles.customTrigger : undefined}
          >
            {trigger ?? <SelectTrigger placeholder={placeholder} />}
          </ArkSelect.Trigger>
        </ArkSelect.Control>
        {error && <ArkSelect.Label className={styles.error}>{error}</ArkSelect.Label>}
        <Portal>
          <ArkSelect.Positioner>
            <ArkSelect.Content
              className={classnames(
                styles.content,
                onlyLightTheme && styles.onlyLightTheme,
              )}
              asChild={!!content}
            >
              {content ?? <SelectContent />}
            </ArkSelect.Content>
          </ArkSelect.Positioner>
        </Portal>
      </ArkSelect.Root>
    </div>
  );
}

function SelectContent() {
  const context = useSelectContext();

  return (
    <>
      {context.collection.group().map(([group, items]) => (
        <ArkSelect.ItemGroup key={group}>
          {group && <ArkSelect.ItemGroupLabel>{group}</ArkSelect.ItemGroupLabel>}
          {items.map((option) => (
            <ArkSelect.Item key={option.value} item={option} className={styles.item}>
              <ArkSelect.ItemText>{option.label}</ArkSelect.ItemText>
            </ArkSelect.Item>
          ))}
        </ArkSelect.ItemGroup>
      ))}
    </>
  );
}

function SelectTrigger({ placeholder }: Pick<SelectProps, "placeholder">) {
  const context = useSelectContext();

  const selectedItems = context.collection.findMany(context.value);
  const hasNonStringsAsLabel = selectedItems.some((item) => typeof item.label !== "string");

  if (hasNonStringsAsLabel) {
    return (
      <>
        {selectedItems.map((item) => item.label)}
        <SelectIndicator />
      </>
    );
  }

  return (
    <>
      <ArkSelect.ValueText placeholder={placeholder} />
      <SelectIndicator />
    </>
  );
}

function SelectIndicator() {
  const context = useSelectContext();

  return (
    <ArkSelect.Indicator className={styles.indicator}>
      <ChevronDown
        size={16}
        style={{
          transform: context.open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }}
      />
    </ArkSelect.Indicator>
  );
}
